import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, Save, Send, Search, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ServiceGrid } from "@/components/onboarding/ServiceGrid";
import { ProposalEditor, AccountingFees } from "@/components/onboarding/ProposalEditor";
import { AgreementPicker, DocTabs } from "@/components/onboarding/AgreementPicker";

const STEP_LABELS = ["Client details", "Services", "Proposal summary", "Review & send"];

export default function OnboardingWizard({ existing, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEdit = !!existing;
  const [step, setStep] = useState(1);
  const [defaults, setDefaults] = useState({ plans: [], add_ons: [] });
  const [availableServices, setAvailableServices] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ client_categories: [], client_types: [], industries: [], annual_txn_bands: [] });

  const [services, setServices] = useState(() => existing?.selected_services || []);
  const [acctAnnual, setAcctAnnual] = useState(existing?.accounting_annual_fee || "");
  const [acctMonthly, setAcctMonthly] = useState(existing?.accounting_monthly_fee || "");

  const [clientCategory, setClientCategory] = useState(existing?.client_category || "");
  const [clientType, setClientType] = useState(existing?.client_type || "");
  const [industry, setIndustry] = useState(existing?.industry || "");
  const [expectedTurnover, setExpectedTurnover] = useState(existing?.expected_turnover ?? "");
  const [annualTransactions, setAnnualTransactions] = useState(existing?.annual_transactions || "");

  const [selectedAddons, setSelectedAddons] = useState(() => new Set((existing?.add_ons || []).map((a) => a.key)));
  const [oneOffFee] = useState(existing?.one_off_fee ?? 0);
  const [oneOffLabel] = useState(existing?.one_off_label || "");

  const [clientName, setClientName] = useState(existing?.client_name || "");
  const [email, setEmail] = useState(existing?.email || "");
  const [phone, setPhone] = useState(existing?.phone || "");
  const [companyNo, setCompanyNo] = useState(existing?.company_number || "");
  const [companyName, setCompanyName] = useState(existing?.company_name || "");
  const [regAddr, setRegAddr] = useState(existing?.registered_address || "");
  const [corrAddr, setCorrAddr] = useState(existing?.correspondence_address || "");
  const [yearEnd, setYearEnd] = useState(existing?.year_end_date || "");
  const [acctMgr, setAcctMgr] = useState(existing?.account_manager || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [chLoading, setChLoading] = useState(false);

  const [proposalHtml, setProposalHtml] = useState(existing?.proposal_html || "");
  const [proposalSubject, setProposalSubject] = useState(existing?.proposal_subject || "");
  const [proposalEdited, setProposalEdited] = useState(!!existing?.proposal_edited);
  const [proposalVersion, setProposalVersion] = useState(0);
  const [proposalLoading, setProposalLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [sendUrl, setSendUrl] = useState(null);
  const [emailed, setEmailed] = useState(false);
  const [savedId, setSavedId] = useState(existing?.id || null);
  const [preview, setPreview] = useState(null);
  const [agreement, setAgreement] = useState({ msa_template_id: existing?.msa_template_id || "", sow_template_id: existing?.sow_template_id || "" });

  function loadCatalogue() {
    api.get("/onboarding/available-services").then((r) => {
      setAvailableServices(r.data.services || []);
      setTaxonomy({
        client_categories: r.data.client_categories || [],
        client_types: r.data.client_types || [],
        industries: r.data.industries || [],
        annual_txn_bands: r.data.annual_txn_bands || [],
      });
    }).catch(() => {});
  }
  useEffect(() => {
    api.get("/onboarding/defaults").then((r) => setDefaults(r.data)).catch(() => {});
    loadCatalogue();
  }, []);

  const chosenAddons = useMemo(() => defaults.add_ons.filter((a) => selectedAddons.has(a.key)), [defaults, selectedAddons]);

  const totals = useMemo(() => {
    let monthly = Number(acctMonthly) || 0, annual = Number(acctAnnual) || 0, oneOff = Number(oneOffFee) || 0;
    let extraMonthly = 0;
    services.filter((s) => s.category !== "accounting").forEach((s) => {
      const f = Number(s.fee) || 0;
      if (s.fee_period === "monthly") { monthly += f; extraMonthly += f; }
      else if (s.fee_period === "annual") annual += f;
      else oneOff += f;
    });
    chosenAddons.forEach((a) => {
      monthly += Number(a.fee_monthly) || 0;
      extraMonthly += Number(a.fee_monthly) || 0;
      oneOff += Number(a.one_off) || 0;
    });
    return { monthly, annual, oneOff, grossAnnual: annual + extraMonthly * 12 + oneOff };
  }, [services, chosenAddons, oneOffFee, acctAnnual, acctMonthly]);

  function toggleService(svc, on) {
    setServices((cur) => {
      if (on) {
        if (cur.find((s) => s.key === svc.key)) return cur;
        return [...cur, { key: svc.key, label: svc.label, category: svc.category, fee: 0, fee_period: "annual", period: svc.periods?.[0] || null, notes: "" }];
      }
      return cur.filter((s) => s.key !== svc.key);
    });
  }
  function updateService(key, patch) {
    setServices((cur) => cur.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  }

  function buildPayload() {
    const catOf = (k) => availableServices.find((x) => x.key === k)?.category;
    const payload = {
      selected_services: services.map((s) => {
        const category = s.category || catOf(s.key) || "accounting";
        return category === "accounting" ? { ...s, category, fee: 0 } : { ...s, category };
      }),
      add_ons: chosenAddons,
      one_off_fee: Number(oneOffFee) || 0,
      one_off_label: oneOffLabel,
      monthly_fee: totals.monthly,
      annual_fee: totals.annual,
      accounting_annual_fee: Number(acctAnnual) || 0,
      accounting_monthly_fee: Number(acctMonthly) || 0,
      client_name: clientName, email, phone,
      company_number: companyNo, company_name: companyName,
      registered_address: regAddr, correspondence_address: corrAddr,
      year_end_date: yearEnd, account_manager: acctMgr, notes,
      client_category: clientCategory, client_type: clientType, industry,
      expected_turnover: expectedTurnover === "" ? null : Number(expectedTurnover),
      annual_transactions: annualTransactions,
      ...agreement,
    };
    if (proposalHtml) Object.assign(payload, { proposal_html: proposalHtml, proposal_subject: proposalSubject, proposal_edited: proposalEdited });
    return payload;
  }

  async function save() {
    if (!clientName.trim() || !email.trim()) { toast.error("Client name and email are required."); setStep(1); return null; }
    if (services.length === 0) { toast.error("Pick at least one service."); setStep(2); return null; }
    setSaving(true);
    try {
      let doc;
      if (savedId) doc = (await api.patch(`/onboarding/${savedId}`, buildPayload())).data;
      else { doc = (await api.post("/onboarding", buildPayload())).data; setSavedId(doc.id); }
      return doc;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      return null;
    } finally { setSaving(false); }
  }

  async function regenerateProposal(id) {
    setProposalLoading(true);
    try {
      const r = await api.post(`/onboarding/${id}/proposal/render`);
      setProposalHtml(r.data.proposal_html);
      setProposalSubject(r.data.proposal_subject);
      setProposalEdited(false);
      setProposalVersion((v) => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build proposal");
    } finally { setProposalLoading(false); }
  }

  async function chLookup() {
    const cn = (companyNo || "").trim();
    if (!cn) { toast.error("Enter a Companies House number first."); return; }
    setChLoading(true);
    try {
      const d = (await api.get(`/companies-house/lookup/${cn}`)).data;
      if (d.company_name) setCompanyName(d.company_name);
      if (!clientName) setClientName(d.company_name || clientName);
      const a = d.registered_office_address || {};
      const addr = [a.premises, a.address_line_1, a.address_line_2, a.locality, a.region, a.postal_code, a.country].filter(Boolean).join(", ");
      if (addr) setRegAddr(addr);
      if (d.accounts?.next_made_up_to) setYearEnd(d.accounts.next_made_up_to);
      toast.success(`Found: ${d.company_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Companies House lookup failed");
    } finally { setChLoading(false); }
  }

  async function doSend() {
    const doc = await save();
    if (!doc) return;
    try {
      const r = await api.post(`/onboarding/${doc.id}/send`);
      const url = r.data?.sign_url || `${window.location.origin}/sign/${r.data.token}`;
      setSendUrl(url);
      setEmailed(!!r.data?.emailed);
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success(r.data?.emailed ? `Proposal emailed to ${doc.email}` : "Marked as sent — signing link copied to clipboard (email not configured).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  }

  // Entering step 3/4 persists the draft; step 3 builds the proposal, step 4 the LoE preview.
  useEffect(() => {
    if (step < 3) return;
    (async () => {
      const doc = await save();
      if (!doc) return;
      if (step === 3 && (!doc.proposal_html || !doc.proposal_edited)) await regenerateProposal(doc.id);
      if (step === 3 && doc.proposal_html && doc.proposal_edited) {
        setProposalHtml(doc.proposal_html);
        setProposalVersion((v) => v + 1);
      }
      if (step === 4) setPreview({ letter: doc.letter_html || "", msa: doc.msa_html || "" });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const hasBusiness = services.some((s) => (s.category || availableServices.find((x) => x.key === s.key)?.category) === "business");
  const acctServices = availableServices.filter((s) => s.category === "accounting");
  const bizServices = availableServices.filter((s) => s.category === "business");

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button data-testid="onboarding-back" onClick={onClose} className="flex items-center gap-1 text-sm text-[var(--ink-2)] hover:text-[var(--ink)] mb-3">
        <ChevronLeft size={14} /> Back to list
      </button>
      <h1 className="text-2xl font-serif-strong mb-1">{isEdit ? "Edit Onboarding" : "New Onboarding"}</h1>
      <p className="text-sm text-[var(--ink-2)] mb-6">Add the client's details, pick their services, check the proposal email, then send.</p>

      <StepIndicator step={step} setStep={setStep} />

      {step === 1 && (
        <div className="space-y-5">
          <div className="card-flat p-5">
            <div className="overline mb-3">Client taxonomy</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Client Category" testid="field-client-category" value={clientCategory} onChange={setClientCategory} items={taxonomy.client_categories} />
              <SelectField label="Client Type" testid="field-client-type" value={clientType} onChange={setClientType} items={taxonomy.client_types} />
              <div>
                <label className="text-xs text-[var(--ink-2)] block mb-1">Industry / Profession</label>
                <input data-testid="field-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} list="industry-list" placeholder="e.g. Healthcare / Doctor" className="input text-sm w-full" />
                <datalist id="industry-list">{taxonomy.industries.map((i) => <option key={i} value={i} />)}</datalist>
              </div>
              <Field label="Expected Turnover (£)" testid="field-turnover" value={expectedTurnover} onChange={setExpectedTurnover} type="number" placeholder="e.g. 150000" />
              <div className="md:col-span-2">
                <label className="text-xs text-[var(--ink-2)] block mb-1">Annual Transactions (approx. count)</label>
                <div className="flex gap-2">
                  {taxonomy.annual_txn_bands.map((b) => (
                    <button key={b} type="button" data-testid={`txn-band-${b}`}
                      onClick={() => setAnnualTransactions(b === annualTransactions ? "" : b)}
                      className={`text-sm px-3 py-1.5 rounded border transition-colors ${annualTransactions === b ? "border-[var(--navy)] bg-[var(--navy)]/5" : "border-[var(--line)] hover:border-[var(--navy)]"}`}
                    >{b}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card-flat p-5">
            <div className="overline mb-3">Companies House lookup</div>
            <div className="flex gap-2">
              <input data-testid="company-number" value={companyNo} onChange={(e) => setCompanyNo(e.target.value)} placeholder="Company number (e.g. 12345678)" className="input text-sm flex-1" />
              <button data-testid="ch-lookup" onClick={chLookup} disabled={chLoading} className="btn-ghost flex items-center gap-1.5 text-sm !py-2 !px-3">
                {chLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Fetch
              </button>
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-2">Leave blank for non-limited clients. Otherwise this pulls name, address and year-end automatically.</div>
          </div>

          <div className="card-flat p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client name (contact person) *" testid="field-client-name" value={clientName} onChange={setClientName} />
            <Field label="Email *" testid="field-email" value={email} onChange={setEmail} type="email" />
            <Field label="Phone" testid="field-phone" value={phone} onChange={setPhone} />
            <Field label="Company name" testid="field-company-name" value={companyName} onChange={setCompanyName} />
            <Field label="Registered address" testid="field-reg-addr" value={regAddr} onChange={setRegAddr} />
            <Field label="Correspondence address (if different)" testid="field-corr-addr" value={corrAddr} onChange={setCorrAddr} />
            <Field label="Year-end date" testid="field-year-end" value={yearEnd} onChange={setYearEnd} placeholder="YYYY-MM-DD" />
            <Field label="Account manager / responsible partner" testid="field-acct-mgr" value={acctMgr} onChange={setAcctMgr} />
            <div className="md:col-span-2">
              <label className="text-xs text-[var(--ink-2)] block mb-1">Internal notes</label>
              <textarea data-testid="field-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input text-sm w-full" />
            </div>
          </div>

          <div className="flex justify-end">
            <button data-testid="step1-next" onClick={() => setStep(2)} className="btn-primary">Next: Services</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="card-flat p-5">
            <div className="overline mb-1">Accounting services</div>
            <p className="text-xs text-[var(--ink-2)] mb-3">Tick everything the client needs. The LoE auto-assembles from your Bright Manager clauses.</p>
            <ServiceGrid services={acctServices} selected={services} onToggle={toggleService} onUpdate={updateService}
              showFee={false} isAdmin={isAdmin} category="accounting" onCatalogueChange={loadCatalogue} />
            <AccountingFees annual={acctAnnual} monthly={acctMonthly}
              onChange={({ annual, monthly }) => { setAcctAnnual(annual); setAcctMonthly(monthly); }} />
          </div>

          <div className="card-flat p-5">
            <div className="overline mb-1">Business services</div>
            <p className="text-xs text-[var(--ink-2)] mb-3">Non-accounting work — consultancy, HR, IT and registered address. Priced per service.</p>
            <ServiceGrid services={bizServices} selected={services} onToggle={toggleService} onUpdate={updateService}
              showFee isAdmin={isAdmin} category="business" onCatalogueChange={loadCatalogue} />
            {hasBusiness && (
              <AgreementPicker msaId={agreement.msa_template_id} sowId={agreement.sow_template_id}
                onChange={(p) => setAgreement((a) => ({ ...a, ...p }))} />
            )}
          </div>

          <AddonsCard addOns={defaults.add_ons} selected={selectedAddons} setSelected={setSelectedAddons} />

          <div className="card-flat p-5 bg-[var(--bg-2)]">
            <div className="overline mb-2">Fees summary</div>
            <div className="grid grid-cols-4 gap-3 text-sm" data-testid="fees-summary">
              <Metric label="Monthly (DD)" value={`£${totals.monthly.toFixed(2)}`} />
              <Metric label="Annual" value={`£${totals.annual.toFixed(2)}`} />
              <Metric label="One-off" value={`£${totals.oneOff.toFixed(2)}`} />
              <Metric label="Gross annual" value={`£${totals.grossAnnual.toFixed(2)}`} big />
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-ghost text-sm !py-2 !px-3">← Back</button>
            <button data-testid="step2-next" onClick={() => setStep(3)} className="btn-primary">Next: Proposal summary</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <ProposalEditor
            subject={proposalSubject}
            onSubject={(v) => { setProposalSubject(v); setProposalEdited(true); }}
            html={proposalHtml}
            version={proposalVersion}
            onChange={(h) => { setProposalHtml(h); setProposalEdited(true); }}
            edited={proposalEdited}
            loading={proposalLoading}
            onRegenerate={() => savedId && regenerateProposal(savedId)}
          />
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-ghost text-sm !py-2 !px-3">← Back</button>
            <button data-testid="step3-next" onClick={() => setStep(4)} className="btn-primary">Next: Review & send</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-5">
          <FeeReview services={services} chosenAddons={chosenAddons} totals={totals} acctAnnual={acctAnnual} acctMonthly={acctMonthly} />
          <div className="card-flat p-5">
            <div className="overline mb-3">Preview — documents the client will sign (Terms link)</div>
            {preview ? <DocTabs letterHtml={preview.letter} msaHtml={preview.msa} /> : <div className="text-xs text-[var(--ink-2)]">Loading preview…</div>}
            {hasBusiness && !agreement.msa_template_id && (
              <div className="text-xs text-[var(--err)] mt-2" data-testid="send-msa-missing">No MSA template picked — go back to Services and choose one before sending.</div>
            )}
          </div>

          {sendUrl ? (
            <SentCard emailed={emailed} email={email} sendUrl={sendUrl} onClose={onClose} />
          ) : (
            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(3)} className="btn-ghost text-sm !py-2 !px-3">← Back</button>
              <div className="flex items-center gap-2">
                <button data-testid="save-draft" onClick={async () => { const d = await save(); if (d) { toast.success("Draft saved"); onClose(); } }} disabled={saving} className="btn-ghost flex items-center gap-1.5 text-sm !py-2 !px-3">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save as draft
                </button>
                <button data-testid="send-engagement" onClick={doSend} disabled={saving} className="btn-primary flex items-center gap-2 text-sm !py-2 !px-3">
                  <Send size={14} /> Send proposal to client
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StepIndicator({ step, setStep }) {
  return (
    <div className="flex items-center gap-2 mb-6 flex-wrap">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        return (
          <div key={n} className="flex items-center">
            <div data-testid={`step-indicator-${n}`} onClick={() => setStep(n)}
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs cursor-pointer transition-colors ${step >= n ? "bg-[var(--navy)] text-white" : "bg-[var(--bg-2)] text-[var(--ink-2)]"}`}
            >{n}</div>
            <div className={`ml-2 text-sm ${step === n ? "text-[var(--ink)] font-medium" : "text-[var(--ink-2)]"}`}>{label}</div>
            {n < STEP_LABELS.length && <div className="w-8 h-px bg-[var(--line)] mx-3" />}
          </div>
        );
      })}
    </div>
  );
}

function AddonsCard({ addOns, selected, setSelected }) {
  return (
    <div className="card-flat p-5">
      <div className="overline mb-3">Add-ons (optional)</div>
      <div className="space-y-2">
        {addOns.map((a) => (
          <label key={a.key} className="flex items-center justify-between py-1.5 border-b border-[var(--line)] last:border-0 cursor-pointer">
            <div className="flex items-center gap-2">
              <input data-testid={`addon-${a.key}`} type="checkbox" checked={selected.has(a.key)}
                onChange={(e) => { const next = new Set(selected); e.target.checked ? next.add(a.key) : next.delete(a.key); setSelected(next); }} />
              <span className="text-sm">{a.label}</span>
            </div>
            <span className="font-mono-data text-sm">{a.one_off > 0 ? `£${a.one_off.toFixed(2)} one-off` : `£${a.fee_monthly.toFixed(2)}/mo`}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FeeReview({ services, chosenAddons, totals, acctAnnual, acctMonthly }) {
  const acct = services.filter((s) => s.category === "accounting");
  const other = services.filter((s) => s.category !== "accounting");
  return (
    <div className="card-flat p-5 bg-[var(--bg-2)]" data-testid="fee-review">
      <div className="overline mb-3">Fee summary</div>
      <div className="text-sm space-y-1">
        {acct.length > 0 && (
          <div className="flex justify-between">
            <span>Accounting: {acct.map((s) => s.label).join(", ")}</span>
            <span className="font-mono-data whitespace-nowrap ml-4">£{Number(acctAnnual || 0).toFixed(2)} / yr · £{Number(acctMonthly || 0).toFixed(2)} / mo</span>
          </div>
        )}
        {other.map((s) => (
          <div key={s.key} className="flex justify-between">
            <span>{s.label}</span><span className="font-mono-data">£{Number(s.fee || 0).toFixed(2)} / {s.fee_period}</span>
          </div>
        ))}
        {chosenAddons.map((a) => (
          <div key={a.key} className="flex justify-between text-[var(--ink-2)]">
            <span>+ {a.label}</span><span className="font-mono-data">{a.one_off > 0 ? `£${a.one_off.toFixed(2)} one-off` : `£${a.fee_monthly.toFixed(2)} / monthly`}</span>
          </div>
        ))}
        <div className="border-t border-[var(--line)] pt-2 mt-2 grid grid-cols-3 gap-2 text-xs">
          <Metric label="Monthly (DD)" value={`£${totals.monthly.toFixed(2)}`} />
          <Metric label="Annual" value={`£${totals.annual.toFixed(2)}`} />
          <Metric label="Gross annual" value={`£${totals.grossAnnual.toFixed(2)}`} big />
        </div>
      </div>
    </div>
  );
}

function SentCard({ emailed, email, sendUrl, onClose }) {
  return (
    <div className="card-flat p-5 border-[var(--ok)] bg-[var(--ok-bg)]" data-testid="sent-card">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 size={18} className="text-[var(--ok)]" />
        <div className="font-medium text-[var(--ok)]">{emailed ? "Proposal emailed to client" : "Proposal marked as sent"}</div>
      </div>
      <div className="text-sm mb-2">
        {emailed
          ? `The client has received the proposal at ${email}. Here's the registration link too (copied to your clipboard):`
          : "Share this registration link with the client — it's also been copied to your clipboard (M365 email isn't configured yet):"}
      </div>
      <input readOnly value={sendUrl} data-testid="sign-url" className="input text-sm w-full font-mono-data" onClick={(e) => e.target.select()} />
      <div className="mt-3 flex justify-end">
        <button data-testid="sent-done" onClick={onClose} className="btn-primary text-sm !py-2 !px-3">Done</button>
      </div>
    </div>
  );
}

function Metric({ label, value, big }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">{label}</div>
      <div className={`font-mono-data ${big ? "text-base font-medium" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function SelectField({ label, testid, value, onChange, items }) {
  return (
    <div>
      <label className="text-xs text-[var(--ink-2)] block mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="input text-sm w-full">
        <option value="">— Select —</option>
        {items.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="text-xs text-[var(--ink-2)] block mb-1">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} type={type} placeholder={placeholder} className="input text-sm w-full" />
    </div>
  );
}
